import { externalApi } from '@on3/ui-lib';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { NoSidebarLayout } from 'components/Layouts/NoSidebarLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Loading } from 'components/UI/Loading/Loading';
import { TextInput } from 'components/UI/TextInput/TextInput';
import { Text } from 'components/UI/Typography/Text';
import type { GetServerSideProps } from 'next';
import { useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import styles from './newsletter.module.scss';

type FormValues = {
  email: string;
};
const required = { required: true };

const On3NewsletterSignup = (props: IGenericPageProps) => {
  const { currentSite } = props?.siteData || {};
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasNewsletter = currentSite?.newsletterListId;

  const { handleSubmit, control } = useForm<FormValues>();
  const subscribeKey = currentSite?.key || 44;

  const onSubmit: SubmitHandler<FormValues> = ({ email }) => {
    setIsLoading(true);
    externalApi
      .post(`users/v1/sites/${subscribeKey}/subscribe-to-newsletter/`, {
        email,
      })
      .then((response) => {
        if (response?.data?.message) {
          setMessage(response?.data?.message);
          setShowMessage(true);
        } else {
          setMessage('Failed to subscribe. Please try again later');
          setShowMessage(true);
        }
      })
      .finally(() => setIsLoading(false));
  };
  const RenderedEmailInput = useCallback(
    //@ts-expect-error look into this typing error
    ({ field }) => (
      <TextInput
        className={styles.input}
        color="primary"
        label="Email Address"
        type="email"
        {...field}
      />
    ),
    [],
  );

  return (
    <PageWrapper
      {...props}
      title={`${
        hasNewsletter ? currentSite?.name : 'On3'
      } Newsletter Sign-up | On3.com`}
    >
      {isLoading && <Loading />}
      <NoSidebarLayout>
        <section className={styles.wrapper}>
          <div className={styles.joinContainer}>
            <header className={styles.header}>
              <Text className={styles.title} variant="h1">
                {`${hasNewsletter ? currentSite?.name : 'On3'}`} Email
                Newsletter
              </Text>
            </header>
            <Text className={styles.subText}>
              Receive Breaking News, In-Depth Analysis via Email
            </Text>

            {showMessage ? (
              <Text className={styles.message} variant="h2">
                {message}
              </Text>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  control={control}
                  name="email"
                  render={RenderedEmailInput}
                  rules={required}
                />
                <Text className={styles.policyText} variant="caption">
                  By subscribing you agree to receive the{' '}
                  {`${hasNewsletter ? currentSite?.name : 'On3'}`} Email
                  Newsletter and other marketing and promotional emails from
                  On3, which may include information from our affiliates and/or
                  partners offers, products and services. For more information
                  about our data practices consult our Privacy Policy
                </Text>
                <PrimaryButton className={styles.submit} type="submit">
                  Subscribe
                </PrimaryButton>
              </form>
            )}
          </div>
        </section>
      </NoSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: 'user',
      siteData: siteData || [],
      userData: userData || {},
    },
  };
};

export default withValidUrl(On3NewsletterSignup);
